// Pairing, configuring and resetting Bluetooth Mesh lamps THROUGH THE HUB —
// the same steps the phone does over its own Bluetooth (meshSession.js), with
// the hub's radio instead of the phone's. Hub firmware 1.1.0+ (docs/20 §6.4).
//
// ⚠️ THE PHONE'S CODE RUNS, NOT A COPY OF IT. configure(), subscribe(),
// readTempRange() and reset() are MeshSession's own methods, borrowed below.
// They only ever call `this.request(dst, opcode, params, devKey, statusOp)`;
// here each such request becomes one hub command, and the hub's answer comes
// back in the SAME byte layout as the lamp's status message (page+composition,
// [status], [status, min, max]). So the order of steps, which models are bound,
// which groups each model listens on — all of it is decided in ONE place for
// both radios, and a fix there fixes both.
//
// Pairing is the one step that is not borrowed: the hub's mesh stack runs the
// whole provisioning exchange itself (MESHPROV), at the address the SERVER
// allocated, and hands back the device key — exactly what pair() returns on
// the phone.
//
// Device keys travel to the hub (MESHKEY) only for a lamp the PHONE paired, and
// it keeps them in RAM only. A device key alone reaches nothing: every mesh
// message is also encrypted with the network key, which never goes over MQTT.

import { HubTransport } from './hubTransport';
import { MeshSession, OP } from './meshSession';
import {
  fromHex, hex, u16le, readU16le,
} from './bytes';

/*
 * Addresses reserved per lamp before pairing. The phone learns a lamp's element
 * count halfway through provisioning and reserves exactly that; the hub's stack
 * needs the address BEFORE it starts. A Telink white lamp has 2 elements — 4
 * leaves room, and a lamp with more takes the extra right after (see pair()).
 */
const HUB_RESERVE_ELEMENTS = 4;

const hex4 = (a) => Number(a).toString(16).toUpperCase().padStart(4, '0');

/** "MESHUNPROV:<uuid>,<mac>,<oob>,<bearers>,<rssi>" -> scan result or null. */
export function parseUnprov(body) {
  const m = /^MESHUNPROV:([0-9a-f]{32}),([0-9a-f]{12}),([0-9A-F]{4}),(\d+),(-?\d+)$/i.exec(String(body || ''));
  if (!m) return null;
  const uuid = m[1].toLowerCase();
  return {
    // The key the add dialog ticks by. The hub has no phone-style BLE id.
    deviceId: `hub-${uuid}`,
    uuid,
    name: `Lamp ${uuid.slice(-4).toUpperCase()}`,
    rssi: Number(m[5]),
    oobInfo: parseInt(m[3], 16),
    lastSeen: Date.now(),
    kind: 'unprovisioned',
    viaHub: true,
  };
}

/** "MESHPROVED:0014,2,<devkey>" -> { address, numElements, devKey } or null. */
export function parseProved(body) {
  const m = /^MESHPROVED:([0-9A-F]{4}),(\d+),([0-9a-f]{32})$/i.exec(String(body || ''));
  if (!m) return null;
  return { address: parseInt(m[1], 16), numElements: Number(m[2]), devKey: m[3].toLowerCase() };
}

/** "MESHCFG:0014,BIND,0" -> status number, or null. */
export const parseCfgStatus = (body) => {
  const m = /^MESHCFG:[0-9A-F]{4},[A-Z]+,(-?\d+)$/i.exec(String(body || ''));
  return m ? Number(m[1]) : null;
};

const TOO_OLD = 'The hub’s firmware is too old for pairing — update it to 1.1.0 or later.';

export class HubMeshSession {
  constructor(hubUno, userId, log = () => {}) {
    this.isHub = true;
    this.hub = new HubTransport(hubUno, userId);
    this.log = log;
    this.devKeys = new Map(); // primary unicast -> { key: Uint8Array, count }
    this.keysOnHub = new Set(); // addresses whose key the hub got this session
    // configure() builds Config AppKey Add from this; the hub adds the
    // location's AppKey it already holds, so nothing real is needed here.
    this.net = { keys: { appKey: new Uint8Array(16) } };
  }

  // ── what MeshSession's borrowed methods expect ────────────────────────────

  setDevKeys(lamps) {
    MeshSession.prototype.setDevKeys.call(this, lamps);
  }

  devKeyFor(a) {
    return MeshSession.prototype.devKeyFor.call(this, a);
  }

  // The hub is always "connected" to the mesh; there is no proxy to find.
  // eslint-disable-next-line class-methods-use-this
  async ensureProxy() {}

  // eslint-disable-next-line class-methods-use-this
  release() {}

  /** One hub command. A firmware older than 1.1.0 answers BADARGS to every new one. */
  async cmd(command, opts) {
    try {
      return await this.hub.send(command, opts);
    } catch (e) {
      if (/did not understand/i.test(e.message)) throw new Error(TOO_OLD);
      throw e;
    }
  }

  /** The device key of a lamp the phone paired, before the first device-key message. */
  async giveKey(address) {
    if (this.keysOnHub.has(address)) return;
    const k = this.devKeyFor(address);
    if (!k) throw new Error('Only the person who paired this lamp (or the owner) can configure it.');
    await this.cmd(`MESHKEY:${hex4(address)},${hex(k)}`);
    this.keysOnHub.add(address);
  }

  /**
   * MeshSession.request(), carried by the hub. Answers in the lamp's own
   * status layout: { opcode: statusOp, src: dst, params }.
   */
  async request(dst, opcode, params, devKey, statusOp) {
    const done = (p) => ({ opcode: statusOp, src: dst, params: p });
    const a = hex4(dst);
    if (devKey) await this.giveKey(dst);
    switch (opcode) {
      case OP.COMP_GET: {
        const body = await this.cmd(`MESHCOMP:${a}`, { expect: 'MESHCOMP:', timeoutMs: 25000 });
        return done(fromHex(body.split(',')[1] || ''));
      }
      case OP.APPKEY_ADD: {
        const body = await this.cmd(`MESHAPPKEY:${a}`, { expect: 'MESHCFG:', timeoutMs: 25000 });
        return done(Uint8Array.of(parseCfgStatus(body) ?? 0xff));
      }
      case OP.BIND: {
        const el = readU16le(params, 0);
        const model = readU16le(params, 4);
        const body = await this.cmd(`MESHBIND:${a},${hex4(el)},${hex4(model)}`, { expect: 'MESHCFG:', timeoutMs: 25000 });
        return done(Uint8Array.of(parseCfgStatus(body) ?? 0xff));
      }
      case OP.SUB_OVERWRITE:
      case OP.SUB_ADD: {
        const el = readU16le(params, 0);
        const group = readU16le(params, 2);
        const model = readU16le(params, 4);
        const ow = opcode === OP.SUB_OVERWRITE ? 1 : 0;
        const body = await this.cmd(`MESHSUB:${a},${hex4(el)},${hex4(group)},${hex4(model)},${ow}`, { expect: 'MESHCFG:', timeoutMs: 15000 });
        return done(Uint8Array.of(parseCfgStatus(body) ?? 0xff));
      }
      case OP.CTL_RANGE_GET: {
        const body = await this.cmd(`MESHRANGE:${a}`, { expect: 'MESHRANGE:', timeoutMs: 10000 });
        const [, st, min, max] = body.slice('MESHRANGE:'.length).split(',').map(Number);
        const p = new Uint8Array(5);
        p[0] = st;
        p.set(u16le(min), 1);
        p.set(u16le(max), 3);
        return done(p);
      }
      case OP.RESET:
        await this.cmd(`MESHRESET:${a}`, { timeoutMs: 15000 });
        this.keysOnHub.delete(dst);
        return done(new Uint8Array(0));
      default:
        throw new Error(`0x${opcode.toString(16)} is not carried by the hub`);
    }
  }

  // ── scanning and pairing ─────────────────────────────────────────────────

  /**
   * Lamps waiting to be paired, as the HUB hears them. Rounds of MESHSCAN until
   * stopped. `onDevice` gets the same shape as the phone's scan. -> stop()
   */
  scan(onDevice, { roundSeconds = 6 } = {}) {
    const state = { stopped: false };
    (async () => {
      while (!state.stopped) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await this.cmd(`MESHSCAN:${roundSeconds}`, {
            timeoutMs: (roundSeconds + 10) * 1000,
            onLine: (body) => {
              const d = parseUnprov(body);
              if (d && !state.stopped) onDevice(d);
            },
          });
        } catch (e) {
          this.log(`Hub scan: ${e.message}`);
          if (e.message === TOO_OLD) { onDevice({ error: e.message }); return; }
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => { setTimeout(r, 2000); });
        }
      }
    })();
    return () => { state.stopped = true; };
  }

  /**
   * Pair one lamp the hub saw. `allocate(count)` -> Promise<address> (the
   * server's AllocateMeshAddress). -> { devKey (hex), address, numElements }
   */
  async pair(device, allocate) {
    const address = await allocate(HUB_RESERVE_ELEMENTS);
    this.log(`Pairing ${device.name} through the hub at ${hex4(address)}…`);
    const body = await this.cmd(`MESHPROV:${device.uuid},${hex4(address)}`, { expect: 'MESHPROVED:', timeoutMs: 75000 });
    const res = parseProved(body);
    if (!res) throw new Error(`The hub answered oddly: ${body}`);
    if (res.numElements > HUB_RESERVE_ELEMENTS) {
      // Keep the server's counter past this lamp's last element.
      await allocate(res.numElements - HUB_RESERVE_ELEMENTS).catch(() => {});
    }
    this.devKeys.set(res.address, { key: fromHex(res.devKey), count: Math.max(1, res.numElements) });
    // The hub provisioned it, so it already holds the key.
    this.keysOnHub.add(res.address);
    return res;
  }
}

// The phone's own steps — see the note at the top.
HubMeshSession.prototype.configure = MeshSession.prototype.configure;
HubMeshSession.prototype.subscribe = MeshSession.prototype.subscribe;
HubMeshSession.prototype.readTempRange = MeshSession.prototype.readTempRange;
HubMeshSession.prototype.reset = MeshSession.prototype.reset;
